import React, { useState, useEffect } from 'react';
import './UserProfile.css';
import TermsOfService from './TermsOfService';
import PrivacyPolicy from './PrivacyPolicy';
import RefundPolicy from './RefundPolicy';
import { apiCall } from '../../utils/api';
import Loading from '../../components/Loading/Loading';
import trackEvent from '../../utils/analytics.js';

// Helper to extract user_id from JWT
const getUserIdFromJwt = () => {
    try {
        const token = document.cookie
            .split('; ')
            .find(row => row.startsWith('access_token='))
            ?.split('=')[1];
        if (!token) return null;
        const payloadBase64 = token.split('.')[1];
        const payload = JSON.parse(atob(payloadBase64));
        return payload.user_id || payload.sub || payload.id || null;
    } catch (e) {
        console.error('Error parsing JWT:', e);
        return null;
    }
};

const DEFAULT_PROFILE_FORM_VALUES = {
    name: '',
    language: '',
    city: '',
    birthday: '',
    role_type: 'CA',
    ca_cs_status: '',
    organisation_name: '',
    coaching_institute: '',
    articleship_location: '',
    total_experience_years: '',
    current_role: '',
    goals: [],
    next_exam_date: '',
};

const UserProfile = ({ hasSubscription = false, onOpenSubscriptionModal, onboardingData = null, onBackFromProfile }) => {
    const [showTerms, setShowTerms] = useState(false);
    const [showPrivacy, setShowPrivacy] = useState(false);
    const [showRefund, setShowRefund] = useState(false);
    const [showPaymentsModal, setShowPaymentsModal] = useState(false);
    const [showSettingsPage, setShowSettingsPage] = useState(false);
    const [showProfileDetails, setShowProfileDetails] = useState(false);
    const [userDetails, setUserDetails] = useState(null);
    const [onboardingDetails, setOnboardingDetails] = useState(null);
    const [subscriptionDetails, setSubscriptionDetails] = useState(null);
    const [payments, setPayments] = useState([]);
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [paymentsError, setPaymentsError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [formValues, setFormValues] = useState(DEFAULT_PROFILE_FORM_VALUES);
    const [savingProfile, setSavingProfile] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [saveMessage, setSaveMessage] = useState('');
    const [activeFieldKey, setActiveFieldKey] = useState(null);

    useEffect(() => {
        fetchUserDetails();
        fetchOnboardingDetails();
        fetchSubscriptionDetails();
    }, []);

    const fetchUserDetails = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiCall.get('/users/get/user/details');
            
            // Handle the response structure based on your API
            const userData = response.data || response;
            setUserDetails(userData);
        } catch (err) {
            console.error('Error fetching user details:', err);
            setError('Failed to load user details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchOnboardingDetails = async () => {
        try {
            const userId = getUserIdFromJwt();
            if (!userId) {
                console.warn('No user_id found in JWT, skipping onboarding fetch');
                return;
            }
            const response = await apiCall.get(`/users/onboarding/${userId}`);
            const onboardingData = response.data || response;
            setOnboardingDetails(onboardingData);
        } catch (err) {
            console.error('Error fetching onboarding details:', err);
            // Don't show error - onboarding data is optional
            setOnboardingDetails(null);
        }
    };

    const fetchSubscriptionDetails = async () => {
        try {
            const response = await apiCall.get('/subscriptions/current');
            const subscriptionData = response.data || response;
            setSubscriptionDetails(subscriptionData);
        } catch (err) {
            console.error('Error fetching subscription details:', err);
            // Don't show error for subscription if user doesn't have one
            setSubscriptionDetails(null);
        }
    };

    const handleLogout = () => {
        trackEvent('logout_clicked');
        document.cookie = 'access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        document.cookie = 'refresh_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        try {
            onLogoutAndroid();
        } catch (error) {
            console.log('Android bridge not available:', error);
        }
        window.location.reload();
    };

    const onLogoutAndroid = () => {
        console.log("Android sign out triggered");
        window.AndroidBridge.triggerLogout();
    };

    const handleTerms = () => {
        setShowSettingsPage(false);
        setShowTerms(true);
    };

    const handlePrivacy = () => {
        setShowSettingsPage(false);
        setShowPrivacy(true);
    };

    const handleRefund = () => {
        setShowSettingsPage(false);
        setShowRefund(true);
    };

    const handleBackFromTerms = () => {
        setShowTerms(false);
        setShowSettingsPage(true);
    };

    const handleBackFromPrivacy = () => {
        setShowPrivacy(false);
        setShowSettingsPage(true);
    };

    const handleBackFromRefund = () => {
        setShowRefund(false);
        setShowSettingsPage(true);
    };

    const handleContactSupport = () => {
        trackEvent('contact_support_clicked');
        try {
            window.AndroidBridge.openDeeplink('https://wa.me/918209561672');
        } catch (error) {
            // Fallback to regular window.open if Android bridge is not available
            window.open('https://wa.me/918209561672', '_blank');
        }
    };

    const handleTransactionHistory = async () => {
        trackEvent('transaction_history_clicked');
        setShowSettingsPage(false);
        setShowPaymentsModal(true);
        await fetchPayments();
    };

    const fetchPayments = async () => {
        try {
            setPaymentsLoading(true);
            setPaymentsError(null);
            const response = await apiCall.get('/payments/list');
            const paymentsData = response.data || response;
            setPayments(Array.isArray(paymentsData) ? paymentsData : []);
        } catch (err) {
            console.error('Error fetching payments:', err);
            setPaymentsError('Failed to load payment history. Please try again.');
            setPayments([]);
        } finally {
            setPaymentsLoading(false);
        }
    };

    const handleClosePaymentsModal = () => {
        setShowPaymentsModal(false);
        setPaymentsError(null);
        setShowSettingsPage(true);
    };

    const handleOpenSettings = () => {
        trackEvent('settings_clicked');
        setShowSettingsPage(true);
    };

    const handleCloseSettings = () => {
        setShowSettingsPage(false);
    };

    const handleOpenProfileDetails = () => {
        trackEvent('profile_details_clicked');
        setSaveError('');
        setSaveMessage('');
        setIsEditingProfile(false);
        setShowProfileDetails(true);
    };

    const handleCloseProfileDetails = () => {
        setShowProfileDetails(false);
        setIsEditingProfile(false);
        setSaveError('');
        setSaveMessage('');
    };

    const handleEditToggle = () => {
        setSaveError('');
        setSaveMessage('');
        setFormValues(buildFormValuesFromData(mergedData));
        setIsEditingProfile(prev => !prev);
    };

    const handleInputChange = (field, value) => {
        setFormValues((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleGoalToggle = (goalId) => {
        setFormValues((prev) => {
            const hasGoal = prev.goals.includes(goalId);
            const goals = hasGoal
                ? prev.goals.filter((goal) => goal !== goalId)
                : [...prev.goals, goalId];
            return { ...prev, goals };
        });
    };

    const handleSaveProfile = async () => {
        setSavingProfile(true);
        setSaveError('');
        setSaveMessage('');
        try {
            const payload = {
                name: formValues.name.trim() || null,
                language: formValues.language || null,
                city: formValues.city.trim() || null,
                birthday: formValues.birthday
                    ? new Date(formValues.birthday).toISOString().split('T')[0]
                    : null,
                role_type: formValues.role_type || null,
                ca_cs_status: formValues.ca_cs_status || null,
                organisation_name: formValues.organisation_name.trim() || null,
                coaching_institute: formValues.coaching_institute.trim() || null,
                articleship_location: formValues.articleship_location.trim() || null,
                total_experience_years:
                    formValues.total_experience_years !== ''
                        ? Number(formValues.total_experience_years)
                        : null,
                current_role: formValues.current_role.trim() || null,
                goals: formValues.goals,
            };

            await apiCall.post('/users/onboarding', payload);
            setSaveMessage('Profile updated successfully.');
            setIsEditingProfile(false);
            setActiveFieldKey(null);
            await fetchOnboardingDetails();
        } catch (err) {
            const message = err?.message || 'Unable to save profile. Please try again.';
            setSaveError(message);
        } finally {
            setSavingProfile(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        } catch (error) {
            return dateString;
        }
    };

    const formatDateTime = (epochTimestamp) => {
        if (!epochTimestamp) return 'N/A';
        try {
            // Handle epoch timestamp (can be in seconds or milliseconds)
            let timestamp = epochTimestamp;
            
            // If timestamp is in seconds (less than a certain threshold), convert to milliseconds
            // Timestamps after 2001 in seconds would be > 1000000000
            // Timestamps in milliseconds would be > 1000000000000
            if (timestamp < 10000000000) {
                timestamp = timestamp * 1000;
            }
            
            const date = new Date(timestamp);
            
            // Validate date
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            
            return date.toLocaleString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'IST'
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Invalid Date';
        }
    };

    const formatCurrency = (amount, currency = 'INR') => {
        if (amount === null || amount === undefined) return 'N/A';
        amount = amount / 100;
        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency || 'USD'
            }).format(amount);
        } catch (error) {
            return `${amount} ${currency || 'USD'}`;
        }
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        const parts = String(name).trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return 'U';
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    };

    // Goal ID to Label mapping (matches onboarding)
    const GOAL_LABELS = {
        'salary': 'Increase salary',
        'exams': 'Clear exams',
        'switch': 'Switch role/company',
        'network': 'Build network',
        'skills': 'Learn new skills',
        'business': 'Start own practice',
    };

    // Language mapping
    const LANGUAGE_LABELS = {
        'english': 'English',
        'hindi': 'हिंदी',
        'gujarati': 'ગુજરાતી',
    };

    // CA/CS Status mapping
    const CA_CS_STATUS_LABELS = {
        'foundation': 'Foundation',
        'intermediate': 'Intermediate',
        'articleship': 'Articleship',
        'finals': 'Finals',
        'cleared': 'Clear kar liya hai',
    };

    const LANGUAGE_OPTIONS = [
        { value: 'english', label: 'English' },
        { value: 'hindi', label: 'Hindi (हिंदी)' },
        { value: 'gujarati', label: 'Gujarati (ગુજરાતી)' },
    ];

    const ROLE_OPTIONS = [
        { value: 'CA', label: 'CA' },
        { value: 'CS', label: 'CS' },
    ];

    const CA_CS_STATUS_OPTIONS = [
        { value: 'foundation', label: 'Foundation' },
        { value: 'intermediate', label: 'Intermediate' },
        { value: 'articleship', label: 'Articleship' },
        { value: 'finals', label: 'Finals' },
        { value: 'cleared', label: 'Clear kar liya hai' },
    ];

    const GOAL_OPTIONS = Object.entries(GOAL_LABELS).map(([value, label]) => ({ value, label }));

    const toIsoDateInput = (value) => {
        if (!value) return '';
        const parsed = new Date(value);
        if (isNaN(parsed.getTime())) return '';
        return parsed.toISOString().split('T')[0];
    };

    const buildFormValuesFromData = (data) => ({
        name: data?.name || '',
        language: data?.language || '',
        city: data?.city || data?.location || '',
        birthday: toIsoDateInput(data?.birthday),
        role_type: data?.role_type || 'CA',
        ca_cs_status: data?.ca_cs_status || '',
        organisation_name: data?.organisation_name || '',
        coaching_institute: data?.coaching_institute || '',
        articleship_location: data?.articleship_location || '',
        total_experience_years: data?.total_experience_years ?? '',
        current_role: data?.current_role || '',
        next_exam_date: toIsoDateInput(data?.next_exam_date),
        goals: Array.isArray(data?.goals)
            ? data.goals
            : Array.isArray(data?.current_goals)
                ? data.current_goals
                : [],
    });

    const onboardingFieldConfigs = [
        {
            key: 'language',
            label: 'Language',
            type: 'select',
            options: LANGUAGE_OPTIONS,
            format: (value) => LANGUAGE_LABELS[value] || value,
        },
        {
            key: 'city',
            label: 'City',
            type: 'text',
        },
        {
            key: 'birthday',
            label: 'Birthday',
            type: 'date',
            format: (value) => formatDate(value),
        },
        {
            key: 'role_type',
            label: 'Role Type',
            type: 'select',
            options: ROLE_OPTIONS,
        },
        {
            key: 'ca_cs_status',
            label: 'Course Level',
            type: 'select',
            options: CA_CS_STATUS_OPTIONS,
            format: (value) => CA_CS_STATUS_LABELS[value] || value,
        },
        {
            key: 'next_exam_date',
            label: 'Next Exam Date',
            type: 'date',
            format: (value) => formatDate(value),
        },
        {
            key: 'organisation_name',
            label: 'Organisation',
            type: 'text',
        },
        {
            key: 'coaching_institute',
            label: 'Coaching Institute',
            type: 'text',
        },
        {
            key: 'articleship_location',
            label: 'Articleship Location',
            type: 'text',
        },
        {
            key: 'total_experience_years',
            label: 'Experience',
            type: 'number',
            format: (value) => {
                if (value === null || value === undefined || value === '') return 'N/A';
                if (Number(value) === 0) return 'Fresher';
                return `${value} years`;
            },
        },
        {
            key: 'current_role',
            label: 'Current Role',
            type: 'text',
        },
    ];

    const getFieldMergedValue = (key) => {
        if (key === 'total_experience_years') {
            return experienceTotalYears;
        }
        if (key === 'birthday') {
            return birthday;
        }
        if (key === 'next_exam_date') {
            return nextExamDate;
        }
        if (key === 'name') {
            return profileName;
        }
        return mergedData?.[key];
    };

    const getFieldDisplayValue = (field) => {
        const value = getFieldMergedValue(field.key);
        if (field.format) {
            const formatted = field.format(value);
            return formatted || 'N/A';
        }
        if (value === null || value === undefined || value === '') {
            return 'N/A';
        }
        if (field.type === 'select' && field.options) {
            const option = field.options.find((opt) => opt.value === value);
            return option?.label || value;
        }
        return value;
    };

    const renderFieldCard = (field) => {
        const editing = activeFieldKey === field.key;
        const editingValue = formValues[field.key] ?? '';

        const inputProps = {
            className: 'profile-edit-input',
            value: editingValue,
            onChange: (e) => handleInputChange(field.key, e.target.value),
        };

        const renderInput = () => {
            if (field.type === 'select') {
                return (
                    <select {...inputProps}>
                        <option value="">Select {field.label.toLowerCase()}</option>
                        {field.options?.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                );
            }

            if (field.type === 'date') {
                return (
                    <input
                        {...inputProps}
                        type="date"
                    />
                );
            }

            if (field.type === 'number') {
                return (
                    <input
                        {...inputProps}
                        type="number"
                        min="0"
                    />
                );
            }

            return (
                <input
                    {...inputProps}
                    type="text"
                />
            );
        };

        return (
            <div className="onboarding-card" key={field.key}>
                <span className="onboarding-label">{field.label}</span>
                {editing ? (
                    renderInput()
                ) : (
                    <span className="onboarding-value">{getFieldDisplayValue(field)}</span>
                )}
            </div>
        );
    };

    // Extract onboarding_choices from the nested structure:
    // API returns: { onboarding_details: { onboarding_choices: { ... } } }
    const resolvedOnboardingData =
        onboardingDetails?.onboarding_details?.onboarding_choices ||
        onboardingDetails?.onboarding_choices ||
        onboardingDetails?.onboarding ||
        onboardingDetails;

    // Merge onboarding data with API data (onboarding takes priority)
    // Use fetched onboardingDetails (choices) and fallback to prop onboardingData
    const mergedData = {
        ...userDetails,
        ...(resolvedOnboardingData || onboardingData),
    };

    // Debug logging
    useEffect(() => {
        console.log('Raw onboardingDetails from API:', onboardingDetails);
        console.log('Resolved onboarding data:', resolvedOnboardingData);
        console.log('Final merged data:', mergedData);
    }, [onboardingDetails, resolvedOnboardingData, mergedData]);

    useEffect(() => {
        if (!activeFieldKey && !isEditingProfile) {
            setFormValues(buildFormValuesFromData(mergedData));
        }
    }, [mergedData, activeFieldKey, isEditingProfile]);

    const profileName = mergedData?.name || null;
    const language = mergedData?.language || null;
    const languageLabel = language ? (LANGUAGE_LABELS[language] || language) : null;
    
    // For CA/CS users, show role_type + ca_cs_status as qualification
    const roleType = mergedData?.role_type || null;
    const caCsStatus = mergedData?.ca_cs_status || null;
    const caCsStatusLabel = caCsStatus ? (CA_CS_STATUS_LABELS[caCsStatus] || caCsStatus) : null;
    const qualification =
        (roleType && caCsStatusLabel) ? `${roleType} (${caCsStatusLabel})` :
        roleType ||
        mergedData?.qualification ||
        mergedData?.role ||
        mergedData?.designation ||
        null;
    
    const city = mergedData?.city || mergedData?.location || null;
    const birthday = mergedData?.birthday || null;
    const nextExamDate = mergedData?.next_exam_date || null;
    const organisationName = mergedData?.organisation_name || null;
    const coachingInstitute = mergedData?.coaching_institute || null;
    const articleshipLocation = mergedData?.articleship_location || null;

    const experienceTotalYears =
        mergedData?.total_experience_years ??
        mergedData?.experience_total_years ??
        mergedData?.experienceYears ??
        null;

    const currentRole =
        mergedData?.current_role ||
        mergedData?.currentRole ||
        mergedData?.role_title ||
        null;

    const goalsFromApi = Array.isArray(mergedData?.goals)
        ? mergedData.goals
        : Array.isArray(mergedData?.current_goals)
            ? mergedData.current_goals
            : [];
    
    // Convert goal IDs to readable labels
    const goals = goalsFromApi.map(goal => GOAL_LABELS[goal] || goal);

    if (showTerms) {
        return <TermsOfService onBack={handleBackFromTerms} />;
    }

    if (showPrivacy) {
        return <PrivacyPolicy onBack={handleBackFromPrivacy} />;
    }

    if (showRefund) {
        return <RefundPolicy onBack={handleBackFromRefund} />;
    }

    // Profile Details Page (shows onboarding-style details)
    if (showProfileDetails) {
        return (
            <div className="user-profile-container new-profile">
                <div className="profile-page-header">
                    <button className="profile-back-btn" onClick={handleCloseProfileDetails}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                    </button>
                    <h2 className="profile-page-title">Profile Details</h2>
                </div>

                <div className="profile-edit-toolbar">
                    <button
                        type="button"
                        className="edit-profile-btn"
                        onClick={handleEditToggle}
                    >
                        {isEditingProfile ? 'Cancel' : 'Edit Profile'}
                    </button>
                    {isEditingProfile && (
                        <button
                            type="button"
                            className="save-profile-btn"
                            onClick={handleSaveProfile}
                            disabled={savingProfile}
                        >
                            {savingProfile ? 'Saving…' : 'Save changes'}
                        </button>
                    )}
                </div>

                {saveMessage && <p className="profile-save-message">{saveMessage}</p>}
                {saveError && <p className="profile-save-error">{saveError}</p>}

                {isEditingProfile && (
                    <div className="profile-edit-form">
                        <div className="profile-form-grid">
                            <label className="profile-edit-field">
                                <span>Name</span>
                                <input
                                    type="text"
                                    className="profile-edit-input"
                                    value={formValues.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    placeholder="Enter your name"
                                />
                            </label>
                            <label className="profile-edit-field">
                                <span>Language</span>
                                <select
                                    className="profile-edit-input"
                                    value={formValues.language}
                                    onChange={(e) => handleInputChange('language', e.target.value)}
                                >
                                    <option value="">Select language</option>
                                    {LANGUAGE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="profile-edit-field">
                                <span>City</span>
                                <input
                                    type="text"
                                    className="profile-edit-input"
                                    value={formValues.city}
                                    onChange={(e) => handleInputChange('city', e.target.value)}
                                    placeholder="eg. Ahmedabad"
                                />
                            </label>
                            <label className="profile-edit-field">
                                <span>Birthday</span>
                                <input
                                    type="date"
                                    className="profile-edit-input"
                                    value={formValues.birthday}
                                    onChange={(e) => handleInputChange('birthday', e.target.value)}
                                />
                            </label>
                            <label className="profile-edit-field">
                                <span>Role Type</span>
                                <select
                                    className="profile-edit-input"
                                    value={formValues.role_type}
                                    onChange={(e) => handleInputChange('role_type', e.target.value)}
                                >
                                    {ROLE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="profile-edit-field">
                                <span>Course Level</span>
                                <select
                                    className="profile-edit-input"
                                    value={formValues.ca_cs_status}
                                    onChange={(e) => handleInputChange('ca_cs_status', e.target.value)}
                                >
                                    <option value="">Select level</option>
                                    {CA_CS_STATUS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="profile-edit-field">
                                <span>Organisation</span>
                                <input
                                    type="text"
                                    className="profile-edit-input"
                                    value={formValues.organisation_name}
                                    onChange={(e) => handleInputChange('organisation_name', e.target.value)}
                                    placeholder="Where do you work?"
                                />
                            </label>
                            <label className="profile-edit-field">
                                <span>Coaching Institute</span>
                                <input
                                    type="text"
                                    className="profile-edit-input"
                                    value={formValues.coaching_institute}
                                    onChange={(e) => handleInputChange('coaching_institute', e.target.value)}
                                    placeholder="Institute name"
                                />
                            </label>
                            <label className="profile-edit-field">
                                <span>Articleship Location</span>
                                <input
                                    type="text"
                                    className="profile-edit-input"
                                    value={formValues.articleship_location}
                                    onChange={(e) => handleInputChange('articleship_location', e.target.value)}
                                    placeholder="eg. Mumbai"
                                />
                            </label>
                            <label className="profile-edit-field">
                                <span>Experience (years)</span>
                                <input
                                    type="number"
                                    min="0"
                                    className="profile-edit-input"
                                    value={formValues.total_experience_years}
                                    onChange={(e) => handleInputChange('total_experience_years', e.target.value)}
                                    placeholder="e.g. 3"
                                />
                            </label>
                            <label className="profile-edit-field">
                                <span>Current Role</span>
                                <input
                                    type="text"
                                    className="profile-edit-input"
                                    value={formValues.current_role}
                                    onChange={(e) => handleInputChange('current_role', e.target.value)}
                                    placeholder="Your role title"
                                />
                            </label>
                        </div>

                        <div className="profile-goals-edit">
                            <span className="profile-edit-label">Goals</span>
                            <div className="goal-checkbox-grid">
                                {GOAL_OPTIONS.map((goal) => (
                                    <label
                                        key={goal.value}
                                        className={`goal-checkbox ${formValues.goals.includes(goal.value) ? 'checked' : ''}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={formValues.goals.includes(goal.value)}
                                            onChange={() => handleGoalToggle(goal.value)}
                                        />
                                        <span>{goal.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                <div className="profile-details-content">
                    <div className="profile-onboarding-grid">
                        {onboardingFieldConfigs.map(renderFieldCard)}
                    </div>

                    {goals.length > 0 && (
                        <div className="profile-detail-item">
                            <span className="profile-detail-label">Goals</span>
                            <div className="profile-goals-tags">
                                {goals.map((goal, idx) => (
                                    <span key={idx} className="profile-goal-tag">{goal}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Settings Page (full page instead of modal)
    if (showSettingsPage) {
        return (
            <div className="user-profile-container new-profile">
                <div className="profile-page-header">
                    <button className="profile-back-btn" onClick={handleCloseSettings}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                    </button>
                    <h2 className="profile-page-title">Settings</h2>
                </div>
                <div className="settings-page-content">
                    {subscriptionDetails && subscriptionDetails.ends_on && (
                        <div className="settings-subscription-info">
                            Subscription ends on <strong>{formatDateTime(subscriptionDetails.ends_on)}</strong>
                        </div>
                    )}
                    <div className="settings-menu-list">
                        <button className="settings-menu-item" type="button" onClick={handleTransactionHistory}>
                            <span>Transaction History</span>
                            <span className="settings-menu-chevron">›</span>
                        </button>
                        <button className="settings-menu-item" type="button" onClick={handleTerms}>
                            <span>Terms of Service</span>
                            <span className="settings-menu-chevron">›</span>
                        </button>
                        <button className="settings-menu-item" type="button" onClick={handlePrivacy}>
                            <span>Privacy Policy</span>
                            <span className="settings-menu-chevron">›</span>
                        </button>
                        <button className="settings-menu-item" type="button" onClick={handleRefund}>
                            <span>Refund Policy</span>
                            <span className="settings-menu-chevron">›</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="user-profile-container">
                <Loading isLoading={true} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="user-profile-container">
                <div className="error-container">
                    <p>{error}</p>
                    <button 
                        className="retry-button" 
                        onClick={fetchUserDetails}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const displayName = profileName || mergedData?.email?.split('@')[0] || 'User';
    const userHandle = displayName ? `@${displayName.toLowerCase().replace(/\s+/g, '')}` : '@user';
    const roleDisplay = qualification || null;
    const handleBackPress = () => {
        if (typeof onBackFromProfile === 'function') {
            onBackFromProfile();
        } else {
            window.history.back();
        }
    };

    const beginFieldEdit = (fieldKey) => {
        setSaveError('');
        setSaveMessage('');
        setFormValues(buildFormValuesFromData(mergedData));
        setActiveFieldKey(fieldKey);
    };

    const cancelFieldEdit = () => {
        setActiveFieldKey(null);
        setSaveError('');
        setSaveMessage('');
        setFormValues(buildFormValuesFromData(mergedData));
    };

    const renderMainFieldCard = (field) => {
        const editing = activeFieldKey === field.key;
        const editingValue = formValues[field.key] ?? '';

        const inputProps = {
            className: 'profile-edit-input',
            value: editingValue,
            onChange: (e) => handleInputChange(field.key, e.target.value),
        };

        const renderInput = () => {
            if (field.type === 'select') {
                return (
                    <select {...inputProps}>
                        <option value="">Select {field.label.toLowerCase()}</option>
                        {field.options?.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                );
            }

            if (field.type === 'date') {
                return (
                    <input
                        {...inputProps}
                        type="date"
                    />
                );
            }

            if (field.type === 'number') {
                return (
                    <input
                        {...inputProps}
                        type="number"
                        min="0"
                        placeholder="0"
                    />
                );
            }

            return (
                <input
                    {...inputProps}
                    type="text"
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                />
            );
        };

        return (
            <div className="onboarding-card" key={field.key}>
                <div className="onboarding-card-header">
                    <span className="onboarding-label">{field.label}</span>
                    {!editing && (
                        <button
                            type="button"
                            className="field-edit-trigger"
                            onClick={() => beginFieldEdit(field.key)}
                            aria-label={`Edit ${field.label}`}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M12 20h9" />
                                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19.99 3 20l.01-4L16.5 3.5z" />
                            </svg>
                        </button>
                    )}
                </div>
                {editing ? (
                    renderInput()
                ) : (
                    <span className="onboarding-value">{getFieldDisplayValue(field)}</span>
                )}
            </div>
        );
    };

    const allFieldConfigs = [
        {
            key: 'name',
            label: 'Name',
            type: 'text',
        },
        ...onboardingFieldConfigs,
    ];
    
    return (
        <div className="user-profile-container new-profile">
            {/* Top Section with Gradient Background */}
            <div className="profile-top-section">
                {/* Navigation Buttons */}
                <div className="profile-nav-row">
                    <button className="profile-nav-btn" type="button" onClick={handleBackPress}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                    </button>
                    <button className="profile-nav-btn" type="button" onClick={handleOpenSettings}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="3"/>
                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                        </svg>
                    </button>
                </div>
                
                {/* Avatar positioned in the gradient area */}
                <div className="profile-avatar-container">
                    <div className="profile-avatar-circle">
                        {getInitials(displayName)}
                    </div>
                </div>
            </div>

            {/* Dark Content Section */}
            <div className="profile-content-section">
                {/* Name and Role */}
                <div className="profile-identity">
                    <h1 className="profile-display-name">{displayName}</h1>
                    <p className="profile-handle">{userHandle}</p>
                    {roleDisplay && <p className="profile-role-line">{roleDisplay}</p>}
                </div>

                {activeFieldKey && (
                    <div className="profile-edit-actions">
                        <button
                            type="button"
                            className="save-profile-btn"
                            onClick={handleSaveProfile}
                            disabled={savingProfile}
                        >
                            {savingProfile ? 'Saving…' : 'Save'}
                        </button>
                        <button
                            type="button"
                            className="cancel-profile-btn"
                            onClick={cancelFieldEdit}
                        >
                            Cancel
                        </button>
                    </div>
                )}

                {saveMessage && <p className="profile-save-message">{saveMessage}</p>}
                {saveError && <p className="profile-save-error">{saveError}</p>}

                {/* Onboarding Details Grid - All Fields */}
                <div className="profile-onboarding-grid">
                    {allFieldConfigs.map(renderMainFieldCard)}
                </div>

                {/* Goals Section */}
                <div className="profile-goals-section">
                    <h3 className="goals-title">Goals</h3>
                    {isEditingProfile ? (
                        <div className="goal-checkbox-grid">
                            {GOAL_OPTIONS.map((goal) => (
                                <label
                                    key={goal.value}
                                    className={`goal-checkbox ${formValues.goals.includes(goal.value) ? 'checked' : ''}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={formValues.goals.includes(goal.value)}
                                        onChange={() => handleGoalToggle(goal.value)}
                                    />
                                    <span>{goal.label}</span>
                                </label>
                            ))}
                        </div>
                    ) : (
                        <div className="goals-tags">
                            {goals.length > 0 ? (
                                goals.map((goal, idx) => (
                                    <span key={idx} className="goal-tag">{goal}</span>
                                ))
                            ) : (
                                <span className="goal-tag" style={{ opacity: 0.5 }}>No goals set</span>
                            )}
                        </div>
                    )}
                </div>

                <div className="profile-trust-section">
                    <p className="profile-trust-text">Aapke jaise aur log CA Circle ko trust karte hain</p>
                </div>

                {/* Bottom Menu - Help, Sign Out */}
                <div className="profile-bottom-menu">
                    <button className="bottom-menu-item" type="button" onClick={handleContactSupport}>
                        <div className="bottom-menu-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                                <line x1="12" y1="17" x2="12.01" y2="17"/>
                            </svg>
                        </div>
                        <span className="bottom-menu-text">Help & Support</span>
                        <span className="bottom-menu-chevron">›</span>
                    </button>
                    <button className="bottom-menu-item logout" type="button" onClick={handleLogout}>
                        <div className="bottom-menu-icon logout-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                                <polyline points="16 17 21 12 16 7"/>
                                <line x1="21" y1="12" x2="9" y2="12"/>
                            </svg>
                        </div>
                        <span className="bottom-menu-text">Sign Out</span>
                        <span className="bottom-menu-chevron">›</span>
                    </button>
                </div>
            </div>

            {showPaymentsModal && (
                <div 
                    className="payments-modal-overlay"
                    onClick={handleClosePaymentsModal}
                >
                    <div 
                        className="payments-modal-content"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="payments-modal-header">
                            <h2 className="payments-modal-title">Transaction History</h2>
                            <button 
                                className="payments-modal-close"
                                onClick={handleClosePaymentsModal}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"/>
                                    <line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            </button>
                        </div>

                        <div className="payments-modal-body">
                            {paymentsLoading ? (
                                <div className="payments-loading">
                                    <Loading isLoading={true} />
                                </div>
                            ) : paymentsError ? (
                                <div className="payments-error">
                                    <p>{paymentsError}</p>
                                    <button 
                                        className="retry-button" 
                                        onClick={fetchPayments}
                                    >
                                        Retry
                                    </button>
                                </div>
                            ) : payments.length === 0 ? (
                                <div className="payments-empty">
                                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                        <polyline points="14,2 14,8 20,8"/>
                                        <line x1="16" y1="13" x2="8" y2="13"/>
                                        <line x1="16" y1="17" x2="8" y2="17"/>
                                    </svg>
                                    <p>No transactions found</p>
                                </div>
                            ) : (
                                <div className="payments-list">
                                    {payments.map((payment, index) => (
                                        <div key={payment._id || payment.id || index} className="payment-item">
                                            <div className="payment-item-header">
                                                <div className="payment-item-icon">
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M20 7h-4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
                                                        <rect x="2" y="7" width="14" height="14" rx="2" ry="2"/>
                                                        <line x1="10" y1="11" x2="10" y2="17"/>
                                                    </svg>
                                                </div>
                                                <div className="payment-item-info">
                                                    <div className="payment-item-amount">
                                                        {formatCurrency(payment.amount, payment.currency)}
                                                    </div>
                                                    <div className="payment-item-date">
                                                        {formatDateTime(payment.c)}
                                                    </div>
                                                </div>
                                            </div>
                                            {payment.description && (
                                                <div className="payment-item-description">
                                                    {payment.description}
                                                </div>
                                            )}
                                            {payment.payment_method && (
                                                <div className="payment-item-method">
                                                    Payment Method: {payment.payment_method}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default UserProfile;
